import { ref } from 'vue'
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  increment
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { TIME_SLOTS, TIME_SLOT_GROUPS, getSlotsByPeriod } from '@/utils/timeSlots'

/**
 * 현장 리스트 광고 관리 Composable
 *
 * 현장 목록 페이지 상단에 최대 5개까지 노출되는 광고
 * 시간대별로 노출되며, 같은 시간대에 여러 광고 동시 노출 가능
 *
 * 광고 상태:
 * - pending: 승인 대기
 * - approved: 승인됨 (노출중)
 * - rejected: 거절됨
 *
 * Firestore 컬렉션: listAds
 * 필드:
 * - partnerId, partnerEmail, partnerDisplayName
 * - propertyId, propertyName
 * - campaignId, campaignCode
 * - imageUrl (광고 이미지 URL)
 * - title (광고 제목)
 * - subtitle (광고 부제목)
 * - region (지역 태그)
 * - timeSlot (시간대 정보)
 * - status, rejectReason
 * - clicks, impressions
 * - duration (1week)
 * - createdAt, updatedAt, approvedAt, rejectedAt
 */
export function useListAd() {
  const loading = ref(false)
  const error = ref(null)

  const MAX_ADS_PER_SLOT = 5 // 시간대별 최대 광고 수

  /**
   * 한국 시간 가져오기
   */
  const getKoreanTime = () => {
    const now = new Date()
    const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
    return koreaTime
  }

  /**
   * 현재 시간대에 활성화된 리스트 광고 조회 (최대 5개)
   */
  const getActiveListAds = async () => {
    loading.value = true
    error.value = null

    try {
      const now = getKoreanTime()
      const currentHour = now.getHours()
      const currentMinutes = now.getMinutes()
      const currentTime = `${String(currentHour).padStart(2, '0')}:${String(currentMinutes).padStart(2, '0')}`

      // approved 상태인 광고 조회
      const q = query(
        collection(db, 'listAds'),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)

      // 현재 시간대에 해당하는 광고만 필터링 (최대 5개)
      const ads = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(ad => {
          if (!ad.timeSlot) return false
          return currentTime >= ad.timeSlot.start && currentTime < ad.timeSlot.end
        })
        .slice(0, MAX_ADS_PER_SLOT)

      return ads
    } catch (err) {
      console.error('ListAd 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 클릭 수 증가
   */
  const incrementClick = async (adId) => {
    try {
      const adRef = doc(db, 'listAds', adId)
      await updateDoc(adRef, {
        clicks: increment(1)
      })
    } catch (err) {
      console.error('클릭 수 증가 실패:', err)
    }
  }

  /**
   * 노출 수 증가
   */
  const incrementImpression = async (adId) => {
    try {
      const adRef = doc(db, 'listAds', adId)
      await updateDoc(adRef, {
        impressions: increment(1)
      })
    } catch (err) {
      console.error('노출 수 증가 실패:', err)
    }
  }

  /**
   * ListAd 신청 (파트너)
   */
  const createListAd = async (adData) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()

      const docRef = await addDoc(collection(db, 'listAds'), {
        ...adData,
        status: 'pending',
        clicks: 0,
        impressions: 0,
        duration: '1week',
        rejectReason: '',
        createdAt: now,
        updatedAt: now
      })

      return docRef.id
    } catch (err) {
      console.error('ListAd 신청 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 내 ListAd 목록 조회 (파트너)
   */
  const getMyListAds = async (partnerId) => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'listAds'),
        where('partnerId', '==', partnerId),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('내 ListAd 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 전체 ListAd 목록 조회 (어드민)
   */
  const getAllListAds = async (statusFilter = null) => {
    loading.value = true
    error.value = null

    try {
      let q
      if (statusFilter) {
        q = query(
          collection(db, 'listAds'),
          where('status', '==', statusFilter),
          orderBy('createdAt', 'desc')
        )
      } else {
        q = query(
          collection(db, 'listAds'),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('ListAd 목록 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * ListAd 상태 변경 (어드민)
   */
  const updateListAdStatus = async (adId, status, rejectReason = '') => {
    loading.value = true
    error.value = null

    try {
      const adRef = doc(db, 'listAds', adId)
      const now = new Date().toISOString()

      const updateData = {
        status,
        updatedAt: now
      }

      if (status === 'approved') {
        updateData.approvedAt = now
      } else if (status === 'rejected') {
        updateData.rejectReason = rejectReason
        updateData.rejectedAt = now
      }

      await updateDoc(adRef, updateData)
      return true
    } catch (err) {
      console.error('ListAd 상태 변경 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * ListAd 취소/삭제 (파트너 - pending 상태만)
   */
  const cancelListAd = async (adId) => {
    loading.value = true
    error.value = null

    try {
      await deleteDoc(doc(db, 'listAds', adId))
      return true
    } catch (err) {
      console.error('ListAd 취소 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 기존 ListAd 확인 (pending 또는 approved 상태)
   */
  const checkExistingListAd = async (partnerId) => {
    try {
      // pending 상태 확인
      const pendingQ = query(
        collection(db, 'listAds'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'pending')
      )
      const pendingSnapshot = await getDocs(pendingQ)
      if (!pendingSnapshot.empty) return true

      // approved 상태 확인
      const approvedQ = query(
        collection(db, 'listAds'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'approved')
      )
      const approvedSnapshot = await getDocs(approvedQ)
      return !approvedSnapshot.empty
    } catch (err) {
      console.error('ListAd 확인 실패:', err)
      return false
    }
  }

  /**
   * 해당 시간대 광고 수 확인 (신청 가능 여부)
   */
  const getSlotAdCount = async (timeSlotId) => {
    try {
      const q = query(
        collection(db, 'listAds'),
        where('status', 'in', ['pending', 'approved']),
        where('timeSlot.id', '==', timeSlotId)
      )
      const snapshot = await getDocs(q)
      return snapshot.size
    } catch (err) {
      console.error('시간대 광고 수 확인 실패:', err)
      return 0
    }
  }

  /**
   * 시간대별 잔여 슬롯 확인
   */
  const getAvailableSlots = async () => {
    try {
      const result = []

      for (const slot of TIME_SLOTS) {
        const count = await getSlotAdCount(slot.id)
        result.push({
          ...slot,
          currentCount: count,
          available: MAX_ADS_PER_SLOT - count,
          isFull: count >= MAX_ADS_PER_SLOT
        })
      }

      return result
    } catch (err) {
      console.error('잔여 슬롯 확인 실패:', err)
      return TIME_SLOTS.map(slot => ({
        ...slot,
        currentCount: 0,
        available: MAX_ADS_PER_SLOT,
        isFull: false
      }))
    }
  }

  return {
    loading,
    error,
    TIME_SLOTS,
    TIME_SLOT_GROUPS,
    getSlotsByPeriod,
    MAX_ADS_PER_SLOT,
    getKoreanTime,
    getActiveListAds,
    incrementClick,
    incrementImpression,
    createListAd,
    getMyListAds,
    getAllListAds,
    updateListAdStatus,
    cancelListAd,
    checkExistingListAd,
    getSlotAdCount,
    getAvailableSlots
  }
}
